// Frontend wrappers around date-fns-tz that always render meeting times in the
// project's IANA timezone instead of the browser's default. Pair with the
// `resolvedTimezone` field exposed on Project from the backend.
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz'
import { format } from 'date-fns'
import { cs, enUS } from 'date-fns/locale'

const LOCALES: Record<string, any> = {
  cs: cs,
  en: enUS,
}

export const DEFAULT_TIMEZONE = 'Europe/Prague'

const safeTz = (timezone?: string | null): string =>
  timezone && timezone.trim() ? timezone : DEFAULT_TIMEZONE

const resolveLocale = (lang?: string): any => {
  if (!lang) return undefined
  const key = lang.split('-')[0].toLowerCase()
  return LOCALES[key]
}

export const formatDateInTz = (
  value: string | Date | null | undefined,
  timezone: string | null | undefined,
  pattern = 'P',
  lang?: string,
): string => {
  if (!value) return '—'
  try {
    return formatInTimeZone(typeof value === 'string' ? new Date(value) : value, safeTz(timezone), pattern, {
      locale: resolveLocale(lang),
    })
  } catch {
    return ''
  }
}

export const formatDateTimeInTz = (
  value: string | Date | null | undefined,
  timezone: string | null | undefined,
  lang?: string,
): string => formatDateInTz(value, timezone, 'PPp', lang)

// Audit timestamps (activity log, "recorded by", "distributed at") reflect when
// a real-world action happened, so they render in the VIEWER's local browser
// timezone — not the project's location timezone (which is for scheduling).
export const formatDateTimeLocal = (
  value: string | Date | null | undefined,
  lang?: string,
  pattern = 'PPp',
): string => {
  if (!value) return '—'
  try {
    return format(typeof value === 'string' ? new Date(value) : value, pattern, {
      locale: resolveLocale(lang),
    })
  } catch {
    return ''
  }
}

export const formatTimeInTz = (
  value: string | Date | null | undefined,
  timezone: string | null | undefined,
  lang?: string,
): string => formatDateInTz(value, timezone, 'p', lang)

// Convert an ISO/UTC moment into the `value` expected by <input type="datetime-local">,
// expressed as wall-clock time in the project's timezone.
export const utcToDatetimeLocal = (
  value: string | Date | null | undefined,
  timezone: string | null | undefined,
): string => {
  if (!value) return ''
  return formatInTimeZone(typeof value === 'string' ? new Date(value) : value, safeTz(timezone), "yyyy-MM-dd'T'HH:mm")
}

// Convert a `datetime-local` string (interpreted as wall-clock in the project
// timezone) back to an ISO/UTC string for transport to the backend.
export const datetimeLocalToUtc = (
  wallClockLocal: string,
  timezone: string | null | undefined,
): string => {
  if (!wallClockLocal) return ''
  return fromZonedTime(wallClockLocal, safeTz(timezone)).toISOString()
}

// For things like recurrenceUntil where only the date matters.
export const utcToDateInput = (
  value: string | Date | null | undefined,
  timezone: string | null | undefined,
): string => {
  if (!value) return ''
  return formatInTimeZone(typeof value === 'string' ? new Date(value) : value, safeTz(timezone), 'yyyy-MM-dd')
}

export const dateInputToUtc = (
  date: string,
  timezone: string | null | undefined,
): string => {
  if (!date) return ''
  return fromZonedTime(`${date}T00:00:00`, safeTz(timezone)).toISOString()
}

// For shifted UI use cases where we want a Date object snapped into the
// project's wall-clock — useful when a third-party datepicker needs a Date.
export const utcToZonedDate = (
  value: string | Date | null | undefined,
  timezone: string | null | undefined,
): Date | null => {
  if (!value) return null
  return toZonedTime(typeof value === 'string' ? new Date(value) : value, safeTz(timezone))
}
